import React from 'react';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import {
    Box,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import CoverageSourceMeter from './CoverageSourceMeter';
import {coverageScoreColor, coverageTrackColor} from '../coverageScoreColor';
import {COVERAGE_SOURCE} from '../coverageSourceTheme';

function HeaderMeter({
    source,
    pct,
    caption,
    testId,
    icon: Icon,
    onShowDetails,
}) {
    const scored = pct != null;
    const pctColor = scored ? coverageScoreColor(pct) : source.color;
    const track = scored ? coverageTrackColor(pct) : source.track;
    return (
        <Box data-testid={testId} sx={{flex: 1, minWidth: 0}}>
            <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Icon sx={{fontSize: 18, color: source.color}} aria-hidden/>
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{color: source.color}}
                    >
                        {source.label}
                    </Typography>
                    <Box flex={1}/>
                    {scored ? (
                        <Typography
                            variant="h6"
                            fontFamily='"Roboto Mono", monospace'
                            fontWeight={700}
                            sx={{color: pctColor, lineHeight: 1, fontSize: '1.35rem'}}
                        >
                            {`${Math.round(pct)}%`}
                        </Typography>
                    ) : (
                        <Typography
                            variant="h6"
                            fontFamily='"Roboto Mono", monospace'
                            fontWeight={500}
                            sx={{color: 'text.secondary', lineHeight: 1, fontSize: '1.05rem'}}
                        >
                            N/A
                        </Typography>
                    )}
                </Stack>
                <CoverageSourceMeter
                    pct={pct}
                    color={pctColor}
                    track={track}
                    height={8}
                />
                <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.45, minHeight: '2.2em'}}>
                    {caption}
                </Typography>
                {onShowDetails ? (
                    <Typography
                        component="button"
                        variant="caption"
                        onClick={onShowDetails}
                        sx={{
                            alignSelf: 'flex-start',
                            p: 0,
                            border: 0,
                            background: 'none',
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontWeight: 600,
                            font: 'inherit',
                            fontSize: '0.75rem',
                        }}
                    >
                        Show details →
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
}

/**
 * Coverage tab snapshot: Data + Analytics meters only.
 * The Both / Data / Analytics lens lives in CoverageSourceLens.
 */
export default function CoverageSnapshotHeader({
    dataPct = null,
    dataCaption,
    analyticPct = null,
    analyticCaption,
    onShowDataDetails,
    onShowAnalyticsDetails,
}) {
    return (
        <Paper variant="outlined" data-testid="coverage-snapshot" sx={{p: 2.5, borderRadius: 2}}>
            <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                    Coverage
                </Typography>
                <Stack
                    direction={{xs: 'column', sm: 'row'}}
                    spacing={3}
                    divider={
                        <Box
                            sx={{
                                display: {xs: 'none', sm: 'block'},
                                width: '1px',
                                bgcolor: 'divider',
                                alignSelf: 'stretch',
                            }}
                        />
                    }
                >
                    <HeaderMeter
                        source={COVERAGE_SOURCE.DATA}
                        pct={dataPct}
                        caption={dataCaption}
                        testId="coverage-meter-data"
                        icon={FolderOutlinedIcon}
                        onShowDetails={onShowDataDetails}
                    />
                    <HeaderMeter
                        source={COVERAGE_SOURCE.ANALYTICS}
                        pct={analyticPct}
                        caption={analyticCaption}
                        testId="coverage-meter-analytics"
                        icon={ArticleOutlinedIcon}
                        onShowDetails={onShowAnalyticsDetails}
                    />
                </Stack>
            </Stack>
        </Paper>
    );
}
